import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Container } from '@components/atoms/Container';
import { useGetFooter } from '@services/useGetFooter';
import styles from './ContactsSection.module.css';

export const ContactsSection: FC = () => {
    const { t } = useTranslation();
    const { footer, request: fetchFooter, loading } = useGetFooter();

    const translations = {
        phoneDescription: t('contacts-section-component.phone-description'),
    };

    useEffect(() => {
        fetchFooter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const address = footer?.content_footer_address;
    const addressParts = address ? address.split(',') : [];
    const phone = footer?.content_footer_phone;
    const email = footer?.content_footer_email;

    const socialNetworks = [
        footer?.content_footer_social_facebook && {
            url: footer.content_footer_social_facebook,
            icon: 'fa-brands fa-facebook-f',
        },
        footer?.content_footer_social_instagram && {
            url: footer.content_footer_social_instagram,
            icon: 'fa-brands fa-instagram',
        },
        footer?.content_footer_social_youtube && {
            url: footer.content_footer_social_youtube,
            icon: 'fa-brands fa-youtube',
        },
        footer?.content_footer_social_x && {
            url: footer.content_footer_social_x,
            icon: 'fa-brands fa-x-twitter',
        },
        footer?.content_footer_social_tiktok && {
            url: footer.content_footer_social_tiktok,
            icon: 'fa-brands fa-tiktok',
        },
    ].filter(Boolean) as { url: string; icon: string }[];

    const hasSocialNetworks = socialNetworks.length > 0;

    if (loading) {
        return null;
    }

    return (
        <section className={`${styles['contacts-section']} bg-secondary text-center`}>
            <Container classes="py-5">
                <div className="row gy-5 g-lg-5 py-lg-5 justify-content-center align-items-center">
                    {address && (
                        <div className="col-6 col-lg-3">
                            <i className={`${styles['icon']} fa-thin fa-location-dot`}></i>
                            <Typography tagVariant={TypographyTagVariants.p} classes="mb-0">
                                {addressParts[0]?.trim()}
                            </Typography>
                            {addressParts.length > 1 && (
                                <Typography tagVariant={TypographyTagVariants.p} classes="mb-0">
                                    {addressParts.slice(1).join(',').trim()}
                                </Typography>
                            )}
                        </div>
                    )}

                    {phone && (
                        <div className="col-6 col-lg-3">
                            <i className={`${styles['icon']} fa-thin fa-phone-volume`}></i>
                            <Typography tagVariant={TypographyTagVariants.p} classes="mb-0">
                                {phone}
                            </Typography>
                            <Typography classes="mb-0" fontSize="12">
                                {translations.phoneDescription}
                            </Typography>
                        </div>
                    )}

                    {email && (
                        <div className="col-6 col-lg-3">
                            <i className={`${styles['icon']} fa-thin fa-envelope`}></i>
                            <Typography tagVariant={TypographyTagVariants.p} classes="mb-0">
                                <Anchor mailTo={email} mode={AnchorMode.none}>
                                    {email}
                                </Anchor>
                            </Typography>
                        </div>
                    )}

                    {hasSocialNetworks && (
                        <div className="col-6 col-lg-3">
                            <i className={`${styles['icon']} fa-thin fa-heart`}></i>
                            <div>
                                <ul className="nav d-inline-flex justify-content-center">
                                    {socialNetworks.map((socialNetwork, index) => (
                                        <li className="mb-2 px-2" key={index}>
                                            <Anchor href={socialNetwork.url} target="_blank" mode={AnchorMode.none}>
                                                <RoundIcon
                                                    icon={socialNetwork.icon}
                                                    variant={RoundIconVariants.variant1}
                                                />
                                            </Anchor>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </Container>
        </section>
    );
};
